import { Chart as ChartJS } from "chart.js";
import React, { useEffect, useState } from "react";
import { Line } from "react-chartjs-2";

import { useTruncatedText } from "../../../../../../../../../components/Text/TruncatedText/internal/useTruncatedText";
import PyTooltip from "../../../../../../../../../components/Tooltip";
import { labelsPlugin } from "./LineChartPlugins";

import { getColor } from "../../../../../../../../../utils/getColor";

import css from "../../Layout/Layout.module.scss";
import { drawTopBorderPlugin } from "../BarChart/BarChartPlugins";
import { Label } from "../UseChartOptions/useChartOptions";

ChartJS.defaults.font.family = "Source Sans Pro, sans-serif";
export interface LineChartProps {
	labels?: Label[];
	datasets?: number[];
	xLabelText?: string;
	yLabelText?: string;
	isCountAggregation?: boolean;
}

// A bar chart that handles avatar images, horizontal, vertical bar and tooltips
const LineChart = React.forwardRef<any, LineChartProps>((props, ref: any) => {
	const { labels, datasets = [], xLabelText, yLabelText, isCountAggregation } = props;
	const [canvasWidth, setCanvasWidth] = useState(0);
	const [canvasHeight, setCanvasHeight] = useState(0);

	const { didTruncateText: didTruncateXLabel } = useTruncatedText(
		xLabelText || "",
		canvasWidth * 0.7 || 500,
		"paragraph-2",
		"regular"
	);
	const { didTruncateText: didTruncateYLabel } = useTruncatedText(
		yLabelText || "",
		canvasHeight * 0.7 || 500,
		"paragraph-2",
		"regular"
	);

	useEffect(() => {
		// Observe size changes
		const resizeObserver = new ResizeObserver((entries) => {
			const { width, height } = entries[0].contentRect;

			// Delay the update to fix the issue where the canvas width are not updated
			requestAnimationFrame(() => {
				setCanvasWidth(width);
				setCanvasHeight(height);
				ref?.current?.update();
			});
		});

		resizeObserver.observe(ref?.current.canvas);

		return () => {
			resizeObserver.disconnect();
		};
	}, [xLabelText, yLabelText, ref?.current]);

	const chartLabels = labels?.slice() || [];

	const tooltip = {
		displayColors: false,
		enabled: true,
		backgroundColor: getColor("neutral", 7),
		callbacks: {
			title: function (context) {
				const labelData = chartLabels[context[0].dataIndex];
				return labelData?.name || "-";
			},
			label: function (context) {
				const labelData = context.raw as number;

				if (isCountAggregation) {
					const formCount = labelData ? labelData.toString() : "0";
					const formLabel = labelData === 1 ? "Form" : "Forms";
					return formCount + " " + formLabel;
				}
				return labelData ? labelData.toFixed(2) : "0";
			},
		},
	};

	const options = {
		responsive: true,
		maintainAspectRatio: false,
		devicePixelRatio: 2,
		spanGaps: true,
		plugins: {
			legend: {
				display: true,
			},
			tooltip,
		},
		scales: {
			y: {
				display: true,
				beginAtZero: true,
				ticks: {
					stepSize: 1,
					padding: 8,
					font: {
						size: 12,
						family: "Source Sans Pro, sans-serif",
					},
				},
				min: 1,
				grid: {
					drawTicks: false,
					color: getColor("neutral", 2),
					lineWidth: 1,
				},
				border: {
					color: getColor("neutral", 2),
				},
			},
			x: {
				beginAtZero: true,
				ticks: {
					padding: 8,
					font: {
						size: 12,
						family: "Source Sans Pro, sans-serif",
					},
					maxTicksLimit: 35,
				},
				grid: {
					drawTicks: false,
					color: getColor("neutral", 2),
					lineWidth: 1,
				},
				border: {
					color: getColor("neutral", 2),
				},
			},
		},
		layout: {
			padding: {
				top: 16,
				left: 50,
				right: 16,
				bottom: 120,
			},
		},
	};

	return (
		<div className={css.relativeContainer}>
			<Line
				data-testid="lineChart"
				ref={ref}
				options={options}
				data={{
					labels: chartLabels.map((label) => label.name),
					datasets: [
						{
							data: datasets,
							backgroundColor: getColor("primary", 5),
							borderColor: getColor("primary", 5),
							pointBackgroundColor: getColor("primary", 5),
							borderWidth: 2,
							clip: 8,
						},
					],
				}}
				plugins={[labelsPlugin(xLabelText, yLabelText), drawTopBorderPlugin]}
			/>

			{didTruncateXLabel && (
				<div className={css.horizontalBarTooltip}>
					<PyTooltip message={xLabelText || ""}>
						<div className={css.tooltipTextPlaceholder} style={{ width: canvasWidth * 0.7 }} />
					</PyTooltip>
				</div>
			)}

			{didTruncateYLabel && (
				<div className={css.barTooltip}>
					<PyTooltip message={yLabelText || ""}>
						<div className={css.tooltipVerticalTextPlacholder} style={{ height: canvasHeight * 0.7 }} />
					</PyTooltip>
				</div>
			)}
		</div>
	);
});

LineChart.displayName = "LineChart";

export default React.memo(LineChart);
